import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  ShiftAttendanceKind,
  ShiftColorToken,
  ShiftDefinition,
  ShiftDefinitionCategory,
  ShiftPaidHoursMode,
} from '../types';
import { organizationApi, shiftDefinitionApi } from '../services/api';
import {
  deriveShiftColorTokens,
  getDefaultShiftColorTokens,
  getShiftToneClass,
  resolveShiftColorFamily,
  resolveShiftColorFamilyFromTokens,
  resolveShiftColorToken,
  SHIFT_COLOR_FAMILIES,
  SHIFT_COLOR_FAMILY_OPTIONS,
  type ShiftColorFamily,
} from '../utils/shiftColorTokens';
import {
  WxbBadge,
  WxbButton,
  WxbCard,
  WxbDataTable,
  WxbIcon,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbRadioGroup,
  WxbSelect,
  WxbSwitch,
  WxbTableActionCell,
  WxbTextarea,
  WxbTimePicker,
} from './wxb-ui';
import './ShiftDefinitionManagement.css';

type FormValues = {
  shift_code: string;
  shift_name: string;
  category: ShiftDefinitionCategory;
  attendance_kind: ShiftAttendanceKind;
  start_time: Dayjs;
  end_time: Dayjs;
  is_cross_day: boolean;
  is_night_shift: boolean;
  color_family: ShiftColorFamily;
  nominal_hours: number;
  paid_hours_mode: ShiftPaidHoursMode;
  paid_hours?: number;
  max_extension_hours?: number;
  description?: string;
  is_active: boolean;
  allow_org_unit_ids: number[];
  deny_org_unit_ids: number[];
};

const categoryLabels: Record<ShiftDefinitionCategory, string> = {
  STANDARD: '标准班次',
  SPECIAL: '特殊班次',
  TEMPORARY: '临时班次',
  LEAVE: '休假班次',
};

const attendanceKindLabels: Record<ShiftAttendanceKind, string> = {
  WORK: '实际工作',
  REST: '休息',
  PAID_LEAVE: '带薪休假',
  UNPAID_LEAVE: '无薪休假',
};

const paidHoursModeLabels: Record<ShiftPaidHoursMode, string> = {
  NOMINAL: '按实际工时',
  NONE: '不计薪',
  FIXED: '固定计薪工时',
  BASE_SHIFT: '按基础班工时',
};

const formatTimeValue = (value?: string) => {
  if (!value) {
    return dayjs('08:00', 'HH:mm');
  }
  const normalized = value.length >= 5 ? value.slice(0, 5) : value;
  return dayjs(normalized, 'HH:mm');
};

const toTimeString = (value: Dayjs) => value?.format('HH:mm');

const ColorFamilyLabel: React.FC<{ family: ShiftColorFamily }> = ({ family }) => {
  const option = SHIFT_COLOR_FAMILIES[family];
  return (
    <span className="shift-definition-color-option">
      <span className="shift-definition-color-pair" aria-hidden="true">
        <span className={`shift-definition-color-swatch ${getShiftToneClass(option.softToken, 'neutral-soft', 'shift-definition-color--')}`} />
        <span className={`shift-definition-color-swatch ${getShiftToneClass(option.solidToken, 'neutral-soft', 'shift-definition-color--')}`} />
      </span>
      <span>{option.label}</span>
    </span>
  );
};

const ColorFamilyPicker: React.FC<{
  value?: ShiftColorFamily | ShiftColorToken | null;
  onChange?: (value: ShiftColorFamily) => void;
}> = ({ value, onChange }) => (
  <WxbRadioGroup
    className="shift-definition-color-picker"
    value={resolveShiftColorFamily(value, 'green')}
    onChange={(next) => onChange?.(resolveShiftColorFamily(next, 'green'))}
    options={SHIFT_COLOR_FAMILY_OPTIONS.map((option) => ({
      label: <ColorFamilyLabel family={option.family} />,
      value: option.family,
    }))}
  />
);

const ColorChip: React.FC<{ label: string; token?: ShiftColorToken | null; fallback: ShiftColorToken }> = ({
  label,
  token,
  fallback,
}) => {
  const resolved = resolveShiftColorToken(token, fallback);
  return (
    <span className="shift-definition-color-chip">
      <span className={`shift-definition-color-dot ${getShiftToneClass(resolved, fallback, 'shift-definition-color--')}`} />
      <span className="shift-definition-color-chip-label">{label}</span>
    </span>
  );
};

const ShiftDefinitionManagement: React.FC = () => {
  const [data, setData] = useState<ShiftDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<ShiftDefinition | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [scopeUnitOptions, setScopeUnitOptions] = useState<Array<{ label: string; value: number }>>([]);
  const [form] = Form.useForm<FormValues>();
  const selectedAttendanceKind = Form.useWatch('attendance_kind', form);
  const selectedNominalHours = Form.useWatch('nominal_hours', form);
  const selectedPaidHoursMode = Form.useWatch('paid_hours_mode', form);
  const isSystemManagedEdit = Boolean(editing?.is_system_managed);
  const baseShiftHours = useMemo(
    () => data.find((shift) => shift.shift_code.trim().toUpperCase() === 'BASE')?.nominal_hours,
    [data],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const list = await shiftDefinitionApi.getAll({ includeInactive });
      setData(list);
    } catch (error: any) {
      console.error('Failed to load shift definitions', error);
      message.error(error?.response?.data?.error ?? '加载班次定义失败');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let active = true;
    const fetchScopeUnits = async () => {
      try {
        const [departments, teams] = await Promise.all([
          organizationApi.getDepartments(),
          organizationApi.getTeams(),
        ]);
        if (!active) return;

        const isActive = (value: boolean | number | undefined) => value === undefined || value === true || value === 1;
        setScopeUnitOptions([
          ...departments
            .filter((department) => department.id && isActive(department.is_active ?? department.isActive))
            .map((department) => ({
              value: department.id!,
              label: `部门 · ${department.dept_name ?? department.deptName ?? department.unit_name ?? department.unitName ?? department.id}`,
            })),
          ...teams
            .filter((team) => team.id && isActive(team.is_active ?? team.isActive))
            .map((team) => ({
              value: team.id!,
              label: `Team · ${team.team_name ?? team.teamName ?? team.unit_name ?? team.unitName ?? team.id}`,
            })),
        ]);
      } catch (error) {
        console.error('Failed to load organization units for shift scope', error);
      }
    };

    fetchScopeUnits();
    return () => {
      active = false;
    };
  }, []);

  const resetForm = useCallback(() => {
    const colorDefaults = getDefaultShiftColorTokens({
      shift_code: 'DAY',
      shift_name: '标准日班',
      nominal_hours: 8,
    });
    form.resetFields();
    form.setFieldsValue({
      category: 'STANDARD',
      attendance_kind: 'WORK',
      is_cross_day: false,
      is_night_shift: false,
      is_active: true,
      start_time: dayjs('08:00', 'HH:mm'),
      end_time: dayjs('17:00', 'HH:mm'),
      nominal_hours: 8,
      paid_hours_mode: 'NOMINAL',
      paid_hours: 0,
      max_extension_hours: 0,
      color_family: resolveShiftColorFamilyFromTokens(colorDefaults),
      allow_org_unit_ids: [],
      deny_org_unit_ids: [],
    });
  }, [form]);

  const openCreateModal = () => {
    setEditing(null);
    resetForm();
    setModalVisible(true);
  };

  const openEditModal = useCallback((record: ShiftDefinition) => {
    setEditing(record);
    const colorDefaults = getDefaultShiftColorTokens(record);
    const fallbackFamily = resolveShiftColorFamilyFromTokens(colorDefaults);
    form.setFieldsValue({
      shift_code: record.shift_code,
      shift_name: record.shift_name,
      category: record.category,
      attendance_kind: record.attendance_kind
        ?? (record.nominal_hours > 0.01 ? 'WORK' : 'REST'),
      start_time: formatTimeValue(record.start_time),
      end_time: formatTimeValue(record.end_time),
      is_cross_day: record.is_cross_day,
      is_night_shift: record.is_night_shift ?? false,
      nominal_hours: record.nominal_hours,
      paid_hours_mode: record.paid_hours_mode
        ?? (record.nominal_hours > 0.01 ? 'NOMINAL' : 'NONE'),
      paid_hours: record.paid_hours ?? 0,
      max_extension_hours: record.max_extension_hours ?? 0,
      color_family: resolveShiftColorFamilyFromTokens(record, fallbackFamily),
      description: record.description ?? '',
      is_active: record.is_active,
      allow_org_unit_ids: (record.org_unit_scopes || [])
        .filter((scope) => scope.scope_type === 'ALLOW')
        .map((scope) => scope.org_unit_id),
      deny_org_unit_ids: (record.org_unit_scopes || [])
        .filter((scope) => scope.scope_type === 'DENY')
        .map((scope) => scope.org_unit_id),
    });
    setModalVisible(true);
  }, [form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const allowOrgUnitIds = values.allow_org_unit_ids || [];
      const denyOrgUnitIds = values.deny_org_unit_ids || [];
      if (
        !isSystemManagedEdit
        && allowOrgUnitIds.some((unitId) => denyOrgUnitIds.includes(unitId))
      ) {
        message.error('同一部门或 Team 不能同时设为允许和禁止');
        return;
      }
      const colorTokens = deriveShiftColorTokens(values.color_family);

      if (editing?.is_system_managed) {
        await shiftDefinitionApi.update(editing.id!, {
          shift_name: values.shift_name,
          display_color_token: colorTokens.display_color_token,
          no_operation_color_token: colorTokens.no_operation_color_token,
          operation_color_token: colorTokens.operation_color_token,
          description: values.description ?? '',
        });
        message.success('系统班次展示信息已更新');
        setModalVisible(false);
        setEditing(null);
        await fetchData();
        return;
      }

      const payload: Omit<ShiftDefinition, 'id' | 'created_at' | 'updated_at'> = {
        shift_code: values.shift_code,
        shift_name: values.shift_name,
        category: values.category,
        attendance_kind: values.attendance_kind,
        start_time: toTimeString(values.start_time),
        end_time: toTimeString(values.end_time),
        is_cross_day: values.is_cross_day,
        is_night_shift: values.is_night_shift ?? false,
        display_color_token: colorTokens.display_color_token,
        no_operation_color_token: colorTokens.no_operation_color_token,
        operation_color_token: colorTokens.operation_color_token,
        nominal_hours: Number(values.nominal_hours),
        paid_hours_mode: values.paid_hours_mode,
        paid_hours: values.paid_hours_mode === 'FIXED' ? Number(values.paid_hours ?? 0) : null,
        max_extension_hours: values.max_extension_hours ?? 0,
        description: values.description ?? '',
        is_active: values.is_active,
        org_unit_scopes: [
          ...allowOrgUnitIds.map((orgUnitId) => ({
            shift_id: editing?.id ?? 0,
            org_unit_id: orgUnitId,
            scope_type: 'ALLOW' as const,
          })),
          ...denyOrgUnitIds.map((orgUnitId) => ({
            shift_id: editing?.id ?? 0,
            org_unit_id: orgUnitId,
            scope_type: 'DENY' as const,
          })),
        ],
      };

      if (editing) {
        await shiftDefinitionApi.update(editing.id!, payload);
        message.success('班次定义已更新');
      } else {
        await shiftDefinitionApi.create(payload);
        message.success('班次定义已创建');
      }

      setModalVisible(false);
      setEditing(null);
      await fetchData();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to save shift definition', error);
      message.error(error?.response?.data?.error ?? '保存班次定义失败');
    }
  };

  const handleDeactivate = useCallback(async (record: ShiftDefinition) => {
    if (!record.id) return;
    try {
      await shiftDefinitionApi.remove(record.id);
      message.success('班次定义已停用');
      await fetchData();
    } catch (error: any) {
      console.error('Failed to deactivate shift definition', error);
      message.error(error?.response?.data?.error ?? '停用失败');
    }
  }, [fetchData]);

  const handleToggleActive = useCallback(async (record: ShiftDefinition, value: boolean) => {
    try {
      await shiftDefinitionApi.update(record.id!, { is_active: value });
      message.success(`班次已${value ? '启用' : '停用'}`);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to toggle shift definition', error);
      message.error(error?.response?.data?.error ?? '更新状态失败');
    }
  }, [fetchData]);

  const columns = useMemo(
    () => [
      {
        title: '班次编码',
        dataIndex: 'shift_code',
        key: 'shift_code',
      },
      {
        title: '班次名称',
        dataIndex: 'shift_name',
        key: 'shift_name',
      },
      {
        title: '类别',
        dataIndex: 'category',
        key: 'category',
        render: (value: ShiftDefinitionCategory) => (
          <WxbBadge
            status={value === 'STANDARD' ? 'info' : value === 'SPECIAL' ? 'neutral' : 'warning'}
            label={categoryLabels[value] ?? value}
            variant="outline"
          />
        ),
      },
      {
        title: '出勤语义',
        dataIndex: 'attendance_kind',
        key: 'attendance_kind',
        render: (value: ShiftAttendanceKind | undefined, record: ShiftDefinition) => {
          const resolved = value ?? (record.nominal_hours > 0.01 ? 'WORK' : 'REST');
          return (
            <span className="wxb-table-inline-list">
              <WxbBadge
                status={resolved === 'WORK' ? 'success' : resolved === 'PAID_LEAVE' ? 'warning' : 'neutral'}
                label={attendanceKindLabels[resolved]}
                variant="bar"
              />
              {record.is_system_managed && <WxbBadge status="info" label="系统管理" variant="bar" />}
            </span>
          );
        },
      },
      {
        title: '时间',
        key: 'time_window',
        render: (_: any, record: ShiftDefinition) => (
          <span className="wxb-table-inline-list">
            <span>{record.start_time} ~ {record.end_time}</span>
            {record.is_cross_day && <WxbBadge status="error" label="跨日" variant="bar" />}
            {record.is_night_shift && <WxbBadge status="info" label="夜班" variant="bar" />}
          </span>
        ),
      },
      {
        title: '实际 / 计薪',
        key: 'hours',
        render: (_: unknown, record: ShiftDefinition) => (
          <span>
            {record.nominal_hours} h / {
              record.paid_hours_mode === 'BASE_SHIFT'
                ? baseShiftHours === undefined
                  ? '未配置（基础班）'
                  : `${baseShiftHours} h（基础班）`
                : record.paid_hours_mode === 'FIXED'
                  ? `${record.paid_hours ?? 0} h`
                  : record.paid_hours_mode === 'NONE'
                    ? '0 h'
                    : `${record.nominal_hours} h`
            }
          </span>
        ),
      },
      {
        title: '加班扩展',
        dataIndex: 'max_extension_hours',
        key: 'max_extension_hours',
        render: (value: number | undefined) => `${value ?? 0} h`,
      },
      {
        title: '适用范围',
        key: 'org_unit_scope',
        render: (_: unknown, record: ShiftDefinition) => {
          const scopes = record.org_unit_scopes || [];
          const allowedCount = scopes.filter((scope) => scope.scope_type === 'ALLOW').length;
          const deniedCount = scopes.filter((scope) => scope.scope_type === 'DENY').length;
          if (scopes.length === 0) {
            return <WxbBadge status="neutral" label="全组织" variant="bar" />;
          }
          return (
            <span className="wxb-table-inline-list">
              {allowedCount > 0 && <WxbBadge status="success" label={`允许 ${allowedCount} 项`} variant="bar" />}
              {deniedCount > 0 && <WxbBadge status="error" label={`禁止 ${deniedCount} 项`} variant="bar" />}
            </span>
          );
        },
      },
      {
        title: '显示颜色',
        key: 'display_colors',
        render: (_: any, record: ShiftDefinition) => {
          const defaults = getDefaultShiftColorTokens(record);
          const fallbackFamily = resolveShiftColorFamilyFromTokens(defaults);
          const family = resolveShiftColorFamilyFromTokens(record, fallbackFamily);
          const derived = deriveShiftColorTokens(family);
          return (
            <div className="shift-definition-color-summary">
              <ColorChip label="无操作" token={derived.no_operation_color_token} fallback={defaults.no_operation_color_token} />
              <ColorChip label="有操作" token={derived.operation_color_token} fallback={defaults.operation_color_token} />
            </div>
          );
        },
      },
      {
        title: '状态',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (value: boolean) => (
          <WxbBadge status={value ? 'success' : 'neutral'} label={value ? '启用' : '停用'} variant="bar" />
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 160,
        render: (_: any, record: ShiftDefinition) => (
          record.is_system_managed
            ? <WxbTableActionCell
                actions={[
                  { key: 'edit', label: '编辑', onClick: () => openEditModal(record) },
                ]}
              />
            : <WxbTableActionCell
            actions={[
              { key: 'edit', label: '编辑', onClick: () => openEditModal(record) },
              record.is_active
                ? {
                    key: 'deactivate',
                    label: '停用',
                    variant: 'danger',
                    onClick: () => handleDeactivate(record),
                    confirm: {
                      title: '确认停用',
                      description: `确认要停用班次【${record.shift_name}】吗？`,
                      okText: '确认停用',
                    },
                  }
                : {
                    key: 'activate',
                    label: '启用',
                    onClick: () => handleToggleActive(record, true),
                  },
            ]}
          />
        ),
      },
    ],
    [baseShiftHours, handleDeactivate, handleToggleActive, openEditModal],
  );

  return (
    <div className="dashboard-page shift-definition-page">
      <WxbCard>
        <h2 className="wxb-h4 shift-definition-title">班次定义管理</h2>
        <p className="wxb-body shift-definition-description">
          在此维护班次的实际出勤与计薪语义。带薪休假不参与操作和连续工作，但可以按基础班折算计薪工时。班次编码需保持站点内唯一，推荐遵循
          <strong> DAY / LONGDAY / NIGHT / REST / PAID_LEAVE </strong>
          等规范。
        </p>
        <div className="shift-definition-toolbar">
          <WxbButton variant="primary" onClick={openCreateModal}>
            <PlusOutlined className="shift-definition-button-icon" />新增班次
          </WxbButton>
          <WxbButton variant="secondary" onClick={fetchData} disabled={loading}>
            <WxbIcon name="refresh" size={14} className="shift-definition-button-icon" />{loading ? '刷新中' : '刷新'}
          </WxbButton>
          <WxbSwitch
            checked={includeInactive}
            onChange={setIncludeInactive}
            checkedChildren="含停用"
            unCheckedChildren="仅启用"
          />
        </div>
      </WxbCard>

      <WxbCard>
        <WxbDataTable
          density="compact"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data}
          emptyState={{ description: includeInactive ? '暂无班次定义' : '暂无启用班次定义' }}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1280 }}
        />
      </WxbCard>

      <WxbModal
        title={editing ? `编辑班次：${editing.shift_name}` : '新增班次定义'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditing(null);
        }}
        onOk={handleSubmit}
        forceRender
        okText="保存"
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={{
            category: 'STANDARD',
            attendance_kind: 'WORK',
            is_cross_day: false,
            is_night_shift: false,
            is_active: true,
            start_time: dayjs('08:00', 'HH:mm'),
            end_time: dayjs('17:00', 'HH:mm'),
            nominal_hours: 8,
            paid_hours_mode: 'NOMINAL',
            paid_hours: 0,
            max_extension_hours: 0,
            color_family: 'green',
            allow_org_unit_ids: [],
            deny_org_unit_ids: [],
          }}
        >
          {isSystemManagedEdit && (
            <WxbBadge
              status="info"
              label="系统班次仅允许修改名称、说明和显示颜色"
              variant="outline"
            />
          )}
          <Form.Item
            name="shift_code"
            label="班次编码"
            rules={[{ required: true, message: '请输入班次编码' }]}
          >
            <WxbInput placeholder="例如 DAY、NIGHT" disabled={Boolean(editing)} />
          </Form.Item>

          <Form.Item
            name="shift_name"
            label="班次名称"
            rules={[{ required: true, message: '请输入班次名称' }]}
          >
            <WxbInput placeholder="例如 日班、夜班" />
          </Form.Item>

          <Form.Item
            name="category"
            label="班次类别"
            rules={[{ required: true, message: '请选择类别' }]}
          >
            <WxbSelect
              disabled={isSystemManagedEdit}
              options={[
                { label: categoryLabels.STANDARD, value: 'STANDARD' },
                { label: categoryLabels.SPECIAL, value: 'SPECIAL' },
                { label: categoryLabels.TEMPORARY, value: 'TEMPORARY' },
                { label: categoryLabels.LEAVE, value: 'LEAVE' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="attendance_kind"
            label="出勤语义"
            rules={[{ required: true, message: '请选择出勤语义' }]}
          >
            <WxbSelect
              disabled={isSystemManagedEdit}
              options={Object.entries(attendanceKindLabels).map(([value, label]) => ({ value, label }))}
              onChange={(value) => {
                const kind = value as ShiftAttendanceKind;
                if (kind !== 'WORK') {
                  form.setFieldsValue({
                    nominal_hours: 0,
                    is_cross_day: false,
                    is_night_shift: false,
                    max_extension_hours: 0,
                  });
                }
                if (kind === 'PAID_LEAVE') {
                  form.setFieldsValue({ category: 'LEAVE', paid_hours_mode: 'BASE_SHIFT' });
                } else if (kind === 'REST' || kind === 'UNPAID_LEAVE') {
                  form.setFieldsValue({ paid_hours_mode: 'NONE' });
                } else {
                  form.setFieldsValue({ paid_hours_mode: 'NOMINAL' });
                }
              }}
            />
          </Form.Item>

          <div className="shift-definition-form-row">
            <Form.Item
              name="start_time"
              label="开始时间"
              rules={[{ required: true, message: '请选择开始时间' }]}
            >
              <WxbTimePicker format="HH:mm" minuteStep={15} disabled={isSystemManagedEdit} />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="结束时间"
              rules={[{ required: true, message: '请选择结束时间' }]}
            >
              <WxbTimePicker format="HH:mm" minuteStep={15} disabled={isSystemManagedEdit} />
            </Form.Item>
          </div>

          <div className="shift-definition-form-row">
            <Form.Item name="is_cross_day" label="跨日班次" valuePropName="checked">
              <WxbSwitch disabled={isSystemManagedEdit || selectedAttendanceKind !== 'WORK'} />
            </Form.Item>
            <Form.Item
              name="is_night_shift"
              label="夜班"
              valuePropName="checked"
              tooltip="标记为夜班后，求解器会应用夜班休息约束"
            >
              <WxbSwitch disabled={isSystemManagedEdit || selectedAttendanceKind !== 'WORK'} />
            </Form.Item>
          </div>

          <div className="shift-definition-form-row">
            <Form.Item
              name="nominal_hours"
              label="实际工作工时 (小时)"
              rules={[{ required: true, message: '请输入折算工时' }]}
            >
              <WxbInputNumber
                min={0}
                step={0.5}
                disabled={isSystemManagedEdit || selectedAttendanceKind !== 'WORK'}
              />
            </Form.Item>
            <Form.Item
              name="max_extension_hours"
              label="最大加班 (小时)"
            >
              <WxbInputNumber
                min={0}
                step={0.5}
                disabled={isSystemManagedEdit || selectedAttendanceKind !== 'WORK'}
              />
            </Form.Item>
          </div>

          <div className="shift-definition-form-row">
            <Form.Item
              name="paid_hours_mode"
              label="计薪工时方式"
              rules={[{ required: true, message: '请选择计薪工时方式' }]}
            >
            <WxbSelect
              disabled={isSystemManagedEdit}
              options={Object.entries(paidHoursModeLabels).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
            {selectedPaidHoursMode === 'FIXED' ? (
              <Form.Item
                name="paid_hours"
                label="固定计薪工时 (小时)"
                rules={[{ required: true, message: '请输入固定计薪工时' }]}
              >
                <WxbInputNumber
                  min={0}
                  step={0.5}
                  disabled={isSystemManagedEdit}
                />
              </Form.Item>
            ) : (
              <Form.Item
                label="当前计薪工时"
                extra={
                  selectedPaidHoursMode === 'BASE_SHIFT'
                    ? baseShiftHours === undefined
                      ? '未找到基础班，请先检查 BASE 班次配置。'
                      : '跟随基础班工时动态变化，不写入固定值。'
                    : selectedPaidHoursMode === 'NONE'
                      ? '该班次不计薪工时。'
                      : '与实际工作工时保持一致。'
                }
              >
                <WxbInputNumber
                  value={
                    selectedPaidHoursMode === 'BASE_SHIFT'
                      ? baseShiftHours
                      : selectedPaidHoursMode === 'NONE'
                        ? 0
                        : selectedNominalHours
                  }
                  placeholder="未配置"
                  suffix="h"
                  disabled
                />
              </Form.Item>
            )}
          </div>

          <div className="shift-definition-color-section">
            <div className="shift-definition-section-title">显示颜色</div>
            <Form.Item
              name="color_family"
              label="班次色系"
              rules={[{ required: true, message: '请选择班次色系' }]}
            >
              <ColorFamilyPicker />
            </Form.Item>
          </div>

          <Form.Item
            name="allow_org_unit_ids"
            label="允许组织范围"
            extra="留空表示不限制；选择部门时自动覆盖其下级 Team。"
          >
            <WxbSelect
              mode="multiple"
              allowClear
              disabled={isSystemManagedEdit}
              placeholder="选择可安排此班的部门或 Team"
              options={scopeUnitOptions}
            />
          </Form.Item>

          <Form.Item
            name="deny_org_unit_ids"
            label="禁止组织范围"
            extra="禁止规则优先于允许规则。"
          >
            <WxbSelect
              mode="multiple"
              allowClear
              disabled={isSystemManagedEdit}
              placeholder="选择不可安排此班的部门或 Team"
              options={scopeUnitOptions}
            />
          </Form.Item>

          <Form.Item name="description" label="说明">
            <WxbTextarea rows={3} placeholder="可填写班次适用范围、注意事项等" />
          </Form.Item>

          <Form.Item name="is_active" label="启用状态" valuePropName="checked">
            <WxbSwitch
              checkedChildren="启用"
              unCheckedChildren="停用"
              disabled={isSystemManagedEdit}
            />
          </Form.Item>
        </Form>
      </WxbModal>

    </div>
  );
};

export default ShiftDefinitionManagement;
